export const SHEETS_PROVIDER_METHODS = Object.freeze([
  "getSheets",
  "createSheet",
  "getSheet",
  "updateSheet",
  "deleteSheet",
  "getSheetUserSettings",
  "saveSheetUserSettings",
  "getSheetUserColumns",
  "saveSheetUserColumns",
  "querySheetContacts",
  "addSheetMembership",
  "removeSheetMembership",
  "updateContactField",
  "deleteWorkspaceContact",
  "deleteSheetField",
  "getSheetRules",
  "createSheetRule",
  "applySheetRule",
  "createSheetImport",
  "getSheetImport",
  "createSheetExport",
  "getSheetExport",
  "getSheetDeliveries",
]);

/**
 * Common provider surface used by the production API adapter and deterministic
 * test fixtures. The application remains JavaScript-only, so this JSDoc shape
 * is the repository-compatible typed contract. Contact row queries return
 * authoritative field maps; contact mutation responses may be partial.
 *
 * @typedef {Object} SheetsProvider
 * @property {(params?: Object, options?: Object) => Promise<Object>} getSheets
 * @property {(data: Object, options?: Object) => Promise<Object>} createSheet
 * @property {(sheet_id: string, options?: Object) => Promise<Object>} getSheet
 * @property {(sheet_id: string, data: Object, options?: Object) => Promise<Object>} updateSheet
 * @property {(sheet_id: string, data: Object, options?: Object) => Promise<Object>} deleteSheet
 * @property {(sheet_id: string, options?: Object) => Promise<Object>} getSheetUserSettings
 * @property {(sheet_id: string, data: Object, options?: Object) => Promise<Object>} saveSheetUserSettings
 * @property {(sheet_id: string, options?: Object) => Promise<Object>} getSheetUserColumns
 * @property {(sheet_id: string, data: Object, options?: Object) => Promise<Object>} saveSheetUserColumns
 * @property {(sheet_id: string, data: Object, options?: Object) => Promise<Object>} querySheetContacts
 * @property {(sheet_id: string, data: Object, options?: Object) => Promise<Object>} addSheetMembership
 * @property {(sheet_id: string, contact_id: string, data: Object, options?: Object) => Promise<Object>} removeSheetMembership
 * @property {(sheet_id: string, contact_id: string, field_id: string|number, data: Object, options?: Object) => Promise<Object>} updateContactField
 * @property {(sheet_id: string, contact_id: string, data: Object, options?: Object) => Promise<Object>} deleteWorkspaceContact
 * @property {(field_id: string|number, data: Object, options?: Object) => Promise<Object>} deleteSheetField
 * @property {(sheet_id: string, options?: Object) => Promise<Object>} getSheetRules
 * @property {(sheet_id: string, data: Object, options?: Object) => Promise<Object>} createSheetRule
 * @property {(sheet_id: string, rule_id: string, data: Object, options?: Object) => Promise<Object>} applySheetRule
 * @property {(sheet_id: string, data: Object, options?: Object) => Promise<Object>} createSheetImport
 * @property {(sheet_id: string, job_id: string, options?: Object) => Promise<Object>} getSheetImport
 * @property {(sheet_id: string, data: Object, options?: Object) => Promise<Object>} createSheetExport
 * @property {(sheet_id: string, job_id: string, options?: Object) => Promise<Object>} getSheetExport
 * @property {(sheet_id: string, options?: Object) => Promise<Object>} getSheetDeliveries
 */

/**
 * Fail early when either provider drifts from the shared contract.
 *
 * @param {SheetsProvider} provider
 * @returns {SheetsProvider}
 */
export function assertSheetsProvider(provider) {
  const missing = SHEETS_PROVIDER_METHODS.filter(
    (method) => typeof provider?.[method] !== "function"
  );

  if (missing.length) {
    throw new TypeError(
      `Invalid Sheets provider: missing ${missing.join(", ")}`
    );
  }

  return provider;
}
